import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { AppDialog, RowActions, StatusBadge, emptyMessage, rowActionsTableStyle } from "../ui";
import { BenefitThumbnail } from "../ui/BenefitThumbnail";
import toast from "react-hot-toast";
import { endOfDay, format, startOfDay } from "date-fns";

import {
  useGetBenefitsQuery,
  useGetBenefitPartnersQuery,
  useDeleteBenefitMutation,
  useDuplicateBenefitMutation,
} from "../../store/services/benefits";
import { useDebounce } from "../../hooks/useDebounce";
import { Benefit, BenefitsParams, BenefitStatus } from "../../models/benefits";
import { BenefitFilters } from "../Filters/BenefitFilters";
import {
  getBenefitDiscountTypeLabel,
  getBenefitStatusLabel,
  getBenefitStatusTone,
} from "../../utils/constants/benefits-constants";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";

const parseBenefitDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatBenefitDate = (value: string | null): string | undefined => {
  const date = parseBenefitDate(value);
  return date ? format(date, "dd/MM/yyyy") : undefined;
};

/**
 * Filtro de vigencia (client-side): la API de /bo/benefits todavía no acepta
 * date_from/date_to, así que el rango se aplica sobre la lista ya cargada.
 * Cada extremo filtra de forma independiente sobre el campo homónimo del
 * beneficio ("Vigencia desde" -> startDate >= filtro, "Vigencia hasta" ->
 * endDate <= filtro); un beneficio sin esa fecha (null) no matchea ese lado.
 */
const isWithinVigenciaRange = (
  benefit: Benefit,
  vigenciaFrom: Date | null,
  vigenciaTo: Date | null
): boolean => {
  if (vigenciaFrom) {
    const start = parseBenefitDate(benefit.startDate);
    if (!start || start < startOfDay(vigenciaFrom)) return false;
  }
  if (vigenciaTo) {
    const end = parseBenefitDate(benefit.endDate);
    if (!end || end > endOfDay(vigenciaTo)) return false;
  }
  return true;
};

const getSortedImages = (benefit: Benefit) =>
  [...(benefit.images ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

export const BenefitListDT = () => {
  const navigate = useNavigate();

  // Filter states
  const [title, setTitle] = useState<string>("");
  const [partnerId, setPartnerId] = useState<string | undefined>();
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [status, setStatus] = useState<BenefitStatus | undefined>();
  const [featured, setFeatured] = useState<boolean | undefined>();
  const [vigenciaFrom, setVigenciaFrom] = useState<Date | null>(null);
  const [vigenciaTo, setVigenciaTo] = useState<Date | null>(null);

  const clearAllFilters = () => {
    setTitle("");
    setPartnerId(undefined);
    setCategoryId(undefined);
    setStatus(undefined);
    setFeatured(undefined);
    setVigenciaFrom(null);
    setVigenciaTo(null);
  };

  const debouncedTitle = useDebounce(title, 500);
  const effectiveTitle = title === "" ? "" : debouncedTitle;

  const [benefitToDelete, setBenefitToDelete] = useState<Benefit | undefined>();
  const [benefitToDuplicate, setBenefitToDuplicate] = useState<
    Benefit | undefined
  >();

  const benefitsParams = React.useMemo<BenefitsParams>(
    () => ({
      partnerId: partnerId || undefined,
      categoryId: categoryId || undefined,
      status,
      featured,
    }),
    [partnerId, categoryId, status, featured]
  );

  // El listado no fuerza tenant: en scope global muestra los beneficios de
  // todos los tenants, que es justamente lo útil de esta pantalla.
  const { data, isLoading } = useGetBenefitsQuery(benefitsParams);
  const { data: partners } = useGetBenefitPartnersQuery({ tenantId: null });

  const partnerNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    (partners ?? []).forEach((partner) =>
      map.set(partner.id, partner.name ?? partner.id)
    );
    return map;
  }, [partners]);

  const filteredBenefits = React.useMemo(() => {
    const term = effectiveTitle.trim().toLowerCase();
    const list = data ?? [];
    return list.filter((benefit) => {
      if (term && !(benefit.title ?? "").toLowerCase().includes(term)) {
        return false;
      }
      return isWithinVigenciaRange(benefit, vigenciaFrom, vigenciaTo);
    });
  }, [data, effectiveTitle, vigenciaFrom, vigenciaTo]);

  const [deleteBenefit, { isLoading: isDeleting }] = useDeleteBenefitMutation();
  const [duplicateBenefit, { isLoading: isDuplicating }] =
    useDuplicateBenefitMutation();

  const handleDeleteBenefit = () => {
    if (!benefitToDelete) return;
    // El beneficio puede ser de otro tenant que el del selector superior: se
    // opera siempre sobre el suyo.
    deleteBenefit({ id: benefitToDelete.id, tenantId: benefitToDelete.tenantId })
      .unwrap()
      .then(() => {
        toast.success("Beneficio eliminado");
      })
      .catch((err) => {
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo eliminar el beneficio")
        );
      })
      .finally(() => setBenefitToDelete(undefined));
  };

  const handleDuplicateBenefit = () => {
    if (!benefitToDuplicate) return;
    duplicateBenefit({
      id: benefitToDuplicate.id,
      tenantId: benefitToDuplicate.tenantId,
    })
      .unwrap()
      .then((copy) => {
        setBenefitToDuplicate(undefined);
        toast.success("Beneficio duplicado");
        navigate(`/benefits-catalog/${copy.id}/edit`);
      })
      .catch((err) => {
        setBenefitToDuplicate(undefined);
        toast.error(
          getAdminFacingApiErrorMessage(err, "No se pudo duplicar el beneficio")
        );
      });
  };

  // Column templates
  const imageBodyTemplate = (benefit: Benefit) => (
    <BenefitThumbnail
      url={getSortedImages(benefit)[0]?.imageUrl}
      alt={benefit.title ?? "Beneficio"}
    />
  );

  const titleBodyTemplate = (benefit: Benefit) => (
    <div className="flex flex-column">
      <span className="font-medium">{benefit.title ?? "-"}</span>
      {benefit.discountLabel && (
        <small className="text-color-secondary">{benefit.discountLabel}</small>
      )}
    </div>
  );

  const partnerBodyTemplate = (benefit: Benefit) =>
    partnerNameById.get(benefit.partnerId) ?? benefit.partnerId ?? "-";

  const discountBodyTemplate = (benefit: Benefit) => {
    if (!benefit.discountType) return "-";
    const label = getBenefitDiscountTypeLabel(benefit.discountType);
    return (
      <Tag
        severity="info"
        value={
          benefit.discountValue ? `${label} · ${benefit.discountValue}` : label
        }
      />
    );
  };

  const validityBodyTemplate = (benefit: Benefit) => {
    const start = formatBenefitDate(benefit.startDate);
    const end = formatBenefitDate(benefit.endDate);
    if (start && end) return `${start} - ${end}`;
    if (start) return `Desde ${start}`;
    if (end) return `Hasta ${end}`;
    return "-";
  };

  const statusBodyTemplate = (benefit: Benefit) => (
    <StatusBadge
      label={getBenefitStatusLabel(benefit.status)}
      tone={getBenefitStatusTone(benefit.status)}
    />
  );

  // 4 acciones: por INC-03, RowActions las renderiza como dropdown "Acciones".
  const actionsBodyTemplate = (benefit: Benefit) => (
    <RowActions
      actions={[
        { icon: "pi pi-eye", label: "Ver", severity: "info", onClick: () => navigate(`${benefit.id}`) },
        { icon: "pi pi-pencil", label: "Editar", onClick: () => navigate(`${benefit.id}/edit`) },
        { icon: "pi pi-copy", label: "Duplicar", severity: "info", onClick: () => setBenefitToDuplicate(benefit) },
        { icon: "pi pi-trash", label: "Eliminar", severity: "danger", onClick: () => setBenefitToDelete(benefit) },
      ]}
    />
  );

  return (
    <>
      <DataTable
        style={rowActionsTableStyle("1rem", "8.25rem")}
        value={filteredBenefits}
        loading={isLoading || isDeleting || isDuplicating}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage={emptyMessage("beneficios")}
        header={
          <BenefitFilters
            title={title}
            setTitle={setTitle}
            partnerId={partnerId}
            setPartnerId={setPartnerId}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            status={status}
            setStatus={setStatus}
            featured={featured}
            setFeatured={setFeatured}
            vigenciaFrom={vigenciaFrom}
            setVigenciaFrom={setVigenciaFrom}
            vigenciaTo={vigenciaTo}
            setVigenciaTo={setVigenciaTo}
            onClearAll={clearAllFilters}
          />
        }
        stripedRows
        sortField="title"
        sortOrder={1}
        removableSort
        dataKey="id"
      >
        <Column
          header="Imagen"
          body={imageBodyTemplate}
          style={{ width: "5rem" }}
          exportable={false}
        />
        <Column
          field="title"
          header="Título"
          body={titleBodyTemplate}
          sortable
          style={{ minWidth: "14rem" }}
        />
        <Column
          header="Partner"
          body={partnerBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Descuento"
          body={discountBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Vigencia"
          body={validityBodyTemplate}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="status"
          header="Estado"
          body={statusBodyTemplate}
          sortable
          style={{ minWidth: "8rem" }}
        />
        <Column
          header={<span className="row-actions-label">Acciones</span>}
          // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
          headerClassName="row-actions-th"
          bodyClassName="row-actions-cell"
          body={actionsBodyTemplate}
          style={{ minWidth: "10rem" }}
          exportable={false}
        />
      </DataTable>

      <AppDialog
        variant="form"
        visible={!!benefitToDuplicate}
        onHide={() => setBenefitToDuplicate(undefined)}
        header="Duplicar beneficio"
        primaryLabel="Duplicar Beneficio"
        onPrimary={handleDuplicateBenefit}
        loading={isDuplicating}
      >
        <div className="flex align-items-center gap-3">
          <i className="pi pi-copy text-4xl text-primary" />
          <span>
            Se creará una copia en borrador de{" "}
            <strong>{benefitToDuplicate?.title ?? "este beneficio"}</strong> con
            todas las imágenes y su configuración.
          </span>
        </div>
      </AppDialog>

      <AppDialog
        variant="destructive"
        visible={!!benefitToDelete}
        onHide={() => setBenefitToDelete(undefined)}
        header="Eliminar beneficio"
        primaryLabel="Eliminar Beneficio"
        onPrimary={handleDeleteBenefit}
        loading={isDeleting}
      >
        <span>
          ¿Estás seguro de que deseás eliminar el beneficio{" "}
          <strong>{benefitToDelete?.title ?? ""}</strong>?
          <br />
          <small className="text-color-secondary">
            Esta acción no se puede deshacer.
          </small>
        </span>
      </AppDialog>
    </>
  );
};
